/**
 * The site's URL grammar, in one place.
 *
 * Real paths, not a hash. The hash form was fine while the whole navigable
 * surface was a chain and a window, but a URL that a server never sees cannot
 * be given a title, cannot be crawled, and cannot be linked to from anywhere
 * that resolves links — and now that a block, a miner and an account each have
 * a page worth sending someone, that matters. Both vhosts already answer any
 * path with `index.html` (`try_files $uri $uri/ /index.html`), so the change is
 * entirely in the client.
 *
 * Segments are explicit — `/quantus/block/13160`, not `/quantus/13160` — rather
 * than discriminated by shape. Shape works until two kinds of thing can look
 * alike, and an SS58 address is arbitrary base58: there is no rule that keeps
 * it clear of a window name forever. Naming the kind costs six characters and
 * removes the whole class of problem.
 */

import type { Window as WindowName } from '../api/generated/Window'

/**
 * The windows, named and labelled by the thing they actually are.
 *
 * The labels used to read `1h`, `6h`, `24h`, `7d`, and the URLs matched. That
 * was a promise the site could not keep: a window is a fixed number of blocks,
 * so how long it reaches back is whatever the chain's rate makes it. Planck's
 * rate fell ninefold when its miners left for mainnet, and `/planck/six_hours`
 * kept saying six hours over a window that spanned twenty-nine — with a miner
 * that had not touched the chain in a day sitting in the standings looking
 * current.
 *
 * So both the label and the URL are the block count now, and the duration is
 * measured and shown beside it rather than baked into a name. `3.6k` is not as
 * friendly as `6h`; it has the advantage of being true on every chain.
 */
export const WINDOWS: { id: WindowName; label: string; blocks: number }[] = [
  { id: '600', label: '600', blocks: 600 },
  { id: '3600', label: '3.6k', blocks: 3600 },
  { id: '14400', label: '14.4k', blocks: 14400 },
  { id: '100800', label: '100.8k', blocks: 100800 },
]

export const DEFAULT_WINDOW: WindowName = '3600'

/**
 * Window names this site used to emit, mapped to what they became.
 *
 * A link somebody sent last week still has to land on the right page. The
 * router rewrites these to the current form rather than serving them, so the
 * address bar never keeps a name the site no longer stands behind.
 */
export const LEGACY_WINDOWS: Record<string, WindowName> = {
  hour: '600',
  six_hours: '3600',
  '6h': '3600',
  day: '14400',
  week: '100800',
}

/**
 * The window as a path segment: `3600-blocks`.
 *
 * The unit is in the segment because the count alone is not a distinct shape —
 * a bare `3600` is also a perfectly good block height, and `isBlockRef` says so
 * two lines before the window is even considered. Shipping `/quantus/3600`
 * sent the standings straight to block 3,600.
 *
 * That is the trap this module's own header warns about: "Shape works until two
 * kinds of thing can look alike... Naming the kind costs six characters and
 * removes the whole class of problem." A window named for its block count had
 * to name the unit too, or it was just a number again.
 */
export function windowPath(window: WindowName): string {
  return `${window}-blocks`
}

/** What the address bar currently says the page should show. */
export interface Route {
  /** `null` means "whichever chain the backend puts first". */
  chain: string | null
  window: WindowName
  /** A height, or a `0x` block hash. */
  block: string | null
  /** A reward preimage. */
  miner: string | null
  /** An SS58 address. */
  account: string | null
  /** A `spec_version`. */
  runtime: number | null
  /** One kind of dispatchable, as `[pallet, call]`. */
  call: [string, string] | null
  /** One kind of event, as `[pallet, variant]`. */
  event: [string, string] | null
  /** A libp2p peer id, identifying one telemetry node. */
  node: string | null
  /** A venue the chain's token is followed on: `near-intents`. */
  market: string | null
  /**
   * A NEAR transaction, by its base58 hash: `/quantus/near/tx/<hash>`.
   *
   * Under a chain although NEAR is not one, because the page is about this
   * chain's token over there — the bridge's mints, the Intents trades — and
   * the API reads it with the chain's own NEAR configuration. `near` is a
   * kind, not a chain: `/near/tx/…` would parse as a chain called "near"
   * (blackbeard/observer#44).
   */
  nearTx: string | null
  /** A NEAR Intents account: `/quantus/near/intents/<account>`. */
  intentsAccount: string | null
  /**
   * An extrinsic, by its hash: `/quantus/tx/0x…` (blackbeard/observer#48).
   * Lower-cased like a block hash, since it is hex.
   */
  tx: string | null
  /**
   * A kind with no value: the index of everything of that kind.
   *
   * `/quantus/runtime/` lists the runtimes, `/quantus/account/` the accounts,
   * `/quantus/block/` the blocks. It is how the routes are discoverable at all
   * — a page addressed by a hash nobody has cannot be found by wandering
   * towards it.
   */
  index: SectionName | null
}

/** The kinds that have an index. */
export type SectionName =
  | 'block'
  | 'call'
  | 'event'
  | 'account'
  | 'reversible'
  | 'state'
  | 'runtime'
  | 'node'
  | 'network'
  | 'wormhole'
  | 'vesting'
  | 'market'
  | 'about'

/**
 * The sections, in the order the nav shows them.
 *
 * `miner` is deliberately absent: the standings *are* the index of miners, and
 * a second page listing the same rows under a different address would be two
 * answers to one question. `/:chain/miner/` redirects there.
 */
export const SECTIONS: { id: SectionName; label: string; header?: boolean }[] = [
  { id: 'block', label: 'Blocks' },
  { id: 'call', label: 'Calls' },
  { id: 'event', label: 'Events' },
  { id: 'account', label: 'Accounts' },
  { id: 'reversible', label: 'In flight' },
  { id: 'state', label: 'State' },
  { id: 'runtime', label: 'Runtimes' },
  // Last, because it is the only section that is not about the chain's own
  // ledger — a node is a machine watching the chain rather than anything the
  // chain records.
  { id: 'node', label: 'Nodes' },
  // Last: the nav reads "this chain's ledger, then the machines watching it,
  // then the economy over all of it".
  { id: 'network', label: 'Network' },
  // Beside the network page because it answers the same kind of question — what
  // is this economy doing — and because the two are read together: the
  // distribution table's blindness is explained here.
  { id: 'wormhole', label: 'Wormhole' },
  // With the economy pages, because a price is a question about the economy —
  // but the only section whose subject is off this chain: what a venue
  // elsewhere settled for its token (blackbeard/observer#32).
  { id: 'market', label: 'Markets' },
  // Last, and the only section that is about the future: every other page here
  // reports what the chain has already done.
  { id: 'vesting', label: 'Vesting' },
  // `header: true` keeps it out of the section nav. It is not a view of the
  // chain — it is about the site — so it sits beside the connection light
  // instead, and is listed here only so the URL parses.
  { id: 'about', label: 'About', header: true },
]

function section(name: string): SectionName | null {
  return SECTIONS.some((s) => s.id === name) ? (name as SectionName) : null
}

export const EMPTY: Route = {
  chain: null,
  window: DEFAULT_WINDOW,
  block: null,
  miner: null,
  account: null,
  runtime: null,
  call: null,
  event: null,
  node: null,
  market: null,
  nearTx: null,
  intentsAccount: null,
  tx: null,
  index: null,
}

function asWindow(segment: string | undefined): WindowName | null {
  // The empty segment is checked first rather than leaning on `&&`: that form
  // returns `''` for an empty path, which is neither a window nor null.
  if (!segment) return null

  const suffixed = segment.endsWith('-blocks') ? segment.slice(0, -'-blocks'.length) : null
  const current = WINDOWS.find((w) => w.id === suffixed)?.id
  if (current) return current

  // A link from before the rename. Resolved here rather than rejected, so an
  // address somebody shared still opens the page it named — `href` will then
  // write the current form back into the bar.
  //
  // Bare counts are deliberately *not* accepted, though the site briefly
  // emitted them: `/quantus/3600` is indistinguishable from block 3,600, and
  // guessing between them would make one of the two silently open the wrong
  // page. A window that cannot be told apart from a height is not a window.
  return LEGACY_WINDOWS[segment] ?? null
}

/**
 * A `spec_version`, which is a bare number.
 *
 * `null` rather than `NaN` for anything else, so a mistyped runtime falls
 * through to the front page like every other unrecognised path rather than
 * rendering a panel that asks the API about `NaN`.
 */
function spec(segment: string): number | null {
  const n = Number(segment.replace(/^v/, ''))
  return Number.isInteger(n) && n >= 0 ? n : null
}

/** A block number, or a 32-byte hash. */
export function isBlockRef(segment: string): boolean {
  return /^\d+$/.test(segment) || /^0x[0-9a-f]{64}$/i.test(segment)
}

/**
 * Parse a pathname into a route.
 *
 * Deliberately total: an unrecognised path degrades to the front page rather
 * than a 404, because every path here is a guess about live chain data — a
 * height that has not happened yet, a chain that was renamed — and a blank
 * error page is a worse answer than the standings.
 */
export function parse(pathname: string): Route {
  const parts = pathname.split('/').filter(Boolean)

  // The legacy chainless miner link, kept working: the preimage identifies the
  // same account on every Quantus chain, so the only thing it lacks is which
  // chain's figures to show, and the default is the right guess.
  if (parts[0] === 'miner' && parts[1]) {
    return { ...EMPTY, miner: parts[1].toLowerCase(), window: asWindow(parts[2]) ?? DEFAULT_WINDOW }
  }
  // Likewise a bare runtime: a spec_version is only meaningful against a chain,
  // and the default chain is the one a link without one means.
  if (parts[0] === 'runtime' && parts[1]) {
    return { ...EMPTY, runtime: spec(parts[1]) }
  }

  const chain = parts[0] ?? null
  if (!chain) return EMPTY

  const kind = parts[1]
  const value = parts[2]
  // `/quantus/near/tx/<hash>` and `/quantus/near/intents/<account>`: two
  // segments of kind, because NEAR has more than one kind of thing to name.
  // A hash is base58 and an account id is case-sensitive in principle, so both
  // pass through exactly as written.
  if (kind === 'near' && parts[3]) {
    if (value === 'tx') return { ...EMPTY, chain, nearTx: parts[3] }
    if (value === 'intents') return { ...EMPTY, chain, intentsAccount: parts[3] }
  }
  if (kind && value) {
    if (kind === 'block') return { ...EMPTY, chain, block: value.toLowerCase() }
    if (kind === 'tx') return { ...EMPTY, chain, tx: value.toLowerCase() }
    // A miner carries a window, because its rank and hashrate are *of* a
    // window and a link to one without it means something different from what
    // the sender was looking at. In the path rather than a query string, so it
    // reads the same way the standings' own window does.
    if (kind === 'miner') {
      return {
        ...EMPTY,
        chain,
        miner: value.toLowerCase(),
        window: asWindow(parts[3]) ?? DEFAULT_WINDOW,
      }
    }
    if (kind === 'account') return { ...EMPTY, chain, account: value }
    if (kind === 'runtime') return { ...EMPTY, chain, runtime: spec(value) }
    // A peer id is base58 and case-sensitive, so unlike a hash or an address it
    // is passed through exactly as written.
    if (kind === 'node') return { ...EMPTY, chain, node: value }
    // `markets` is accepted as well as the canonical `market`: it is what a
    // person naturally types for a list of them, and App rewrites it.
    if (kind === 'market' || kind === 'markets') return { ...EMPTY, chain, market: value }
    // `/quantus/call/Balances/transfer_allow_death` — a pallet and a call, both
    // as the runtime names them, because those names are the identity and any
    // slug of ours would be a second vocabulary to keep in step.
    if (kind === 'call' && parts[3]) return { ...EMPTY, chain, call: [value, parts[3]] }
    if (kind === 'event' && parts[3]) return { ...EMPTY, chain, event: [value, parts[3]] }
  }
  // A kind with nothing after it — `/quantus/account` or `/quantus/account/`,
  // which `filter(Boolean)` above makes the same path. The index of that kind.
  if (kind && !value) {
    const named = section(kind === 'markets' ? 'market' : kind)
    if (named) return { ...EMPTY, chain, index: named }
    // The standings are the miner index; there is no second page of them.
    if (kind === 'miner') return { ...EMPTY, chain }
  }
  // A bare height or hash as the second segment: the shape the site used
  // before this module existed. Recognised so links already sent stay good.
  if (kind && isBlockRef(kind)) return { ...EMPTY, chain, block: kind.toLowerCase() }

  return { ...EMPTY, chain, window: asWindow(kind) ?? DEFAULT_WINDOW }
}

/**
 * Whether a route names no subject, and is therefore the standings.
 *
 * One definition, because there were three and adding a section only updated
 * two of them: `/quantus/node/<peer>` was judged "the standings", and the
 * canonicalising redirect in `App` bounced it to `/quantus/3600-blocks` before
 * it could render. The page existed, the route parsed, and the address bar
 * threw it away.
 *
 * Written as a check over every subject field rather than a list of the ones
 * that happened to exist when it was written — a new one still has to be added
 * here, but here is one place instead of three.
 */
export function isStandings(route: Route): boolean {
  return (
    route.index === null &&
    route.block === null &&
    route.miner === null &&
    route.account === null &&
    route.runtime === null &&
    route.call === null &&
    route.event === null &&
    route.node === null &&
    route.market === null &&
    route.nearTx === null &&
    route.intentsAccount === null &&
    route.tx === null
  )
}

/**
 * The section a route belongs to, for the nav to mark.
 *
 * A subject's own page belongs to the section that indexes it:
 * `/quantus/market/near-intents` is in Markets as much as `/quantus/market`
 * is. The nav used to test the index plus a hand-kept list of exceptions, and
 * every section added after the list was written — markets, blocks, accounts,
 * runtimes — went unmarked on its own pages. Same lesson as {@link isStandings}:
 * one place, written over every subject field.
 *
 * Block zero is the exception: the nav has a Genesis link for exactly that
 * block, and marking Blocks beside it would mark two places at once. A miner
 * belongs to no section — the standings are its index, and they have their own
 * link.
 */
export function sectionOf(route: Route): SectionName | null {
  if (route.index) return route.index
  if (route.block !== null) return route.block === '0' ? null : 'block'
  if (route.account !== null) return 'account'
  // An extrinsic is part of a block, and the block index is how it is reached.
  if (route.tx !== null) return 'block'
  if (route.runtime !== null) return 'runtime'
  if (route.call !== null) return 'call'
  if (route.event !== null) return 'event'
  if (route.node !== null) return 'node'
  if (route.market !== null) return 'market'
  // The token on NEAR, like the market pages that link to them.
  if (route.nearTx !== null || route.intentsAccount !== null) return 'market'
  return null
}

/** The canonical path for a route. The inverse of {@link parse}. */
export function href(route: Partial<Route>): string {
  const chain = route.chain ?? ''
  if (route.tx) return `/${chain}/tx/${route.tx}`
  if (route.nearTx) return `/${chain}/near/tx/${route.nearTx}`
  if (route.intentsAccount) return `/${chain}/near/intents/${route.intentsAccount}`
  if (route.node) return `/${chain}/node/${route.node}`
  if (route.market) return `/${chain}/market/${route.market}`
  if (route.block) return `/${chain}/block/${route.block}`
  if (route.miner) {
    const window = windowPath(route.window ?? DEFAULT_WINDOW)
    return chain ? `/${chain}/miner/${route.miner}/${window}` : `/miner/${route.miner}/${window}`
  }
  if (route.call) return `/${chain}/call/${route.call[0]}/${route.call[1]}`
  if (route.event) return `/${chain}/event/${route.event[0]}/${route.event[1]}`
  if (route.index) return `/${chain}/${route.index}`
  if (route.account) return `/${chain}/account/${route.account}`
  if (route.runtime !== null && route.runtime !== undefined) {
    return chain ? `/${chain}/runtime/${route.runtime}` : `/runtime/${route.runtime}`
  }
  return `/${chain}/${windowPath(route.window ?? DEFAULT_WINDOW)}`
}

/**
 * The path a legacy hash URL meant, or `null` if there is nothing to migrate.
 *
 * Every link to this site published before today is `#/quantus/day` or
 * `#/quantus/1069799`, and a fragment is never sent to the server, so nothing
 * but the page itself can honour them. Rewriting on load with `replaceState`
 * keeps them working without leaving the old form in the back stack.
 */
export function fromLegacyHash(hash: string): string | null {
  if (!hash.startsWith('#/')) return null
  return href(parse(hash.slice(1)))
}
